import { API_BASE_URL, API_ENDPOINTS } from '@/lib/config'
import { authenticatedFetch, getAuthToken } from '@/lib/auth'
import type {
  SupportLookupEntry,
  SupportLookupResult,
  SupportSession,
  SupportThreadListItem,
} from './types'

const HEADER_SUPPORT_TOKEN = 'X-Support-Token'

type GoEnvelope<T> = {
  status: number
  data?: T
  message?: string | null
  error?: string | null
}

function apiUrl(path: string): string {
  if (!API_BASE_URL) {
    throw new Error('API is not configured')
  }
  return `${API_BASE_URL}${path}`
}

function unwrapData<T>(body: GoEnvelope<T>, context: string): T {
  if (body.error) {
    throw new Error(typeof body.error === 'string' ? body.error : context)
  }
  if (body.data === undefined || body.data === null) {
    throw new Error(body.message || context)
  }
  return body.data
}

export async function supportRequest(
  path: string,
  init: RequestInit & { writeToken?: string | null } = {},
): Promise<Response> {
  const { writeToken, ...rest } = init
  const headers = new Headers(rest.headers)
  if (
    !headers.has('Content-Type') &&
    rest.body != null &&
    !(rest.body instanceof FormData)
  ) {
    headers.set('Content-Type', 'application/json')
  }
  const t = getAuthToken()
  if (t) {
    headers.set('Authorization', `Bearer ${t}`)
  }
  if (writeToken) {
    headers.set(HEADER_SUPPORT_TOKEN, writeToken)
  }
  return fetch(apiUrl(path), { ...rest, headers })
}

async function parseError(res: Response, fallback: string): Promise<never> {
  const text = await res.text()
  let msg = `${fallback} (${res.status})`
  try {
    const b = JSON.parse(text) as GoEnvelope<unknown> & { detail?: string }
    if (b.error && typeof b.error === 'string') msg = b.error
    else if (b.detail && typeof b.detail === 'string') msg = b.detail
  } catch {
    if (text) msg = text.slice(0, 200)
  }
  throw new Error(msg)
}

function asSession(data: unknown): SupportSession {
  return data as SupportSession
}

export async function postCreateThread(): Promise<SupportSession> {
  const res = await supportRequest(API_ENDPOINTS.SUPPORT_THREADS_BASE, {
    method: 'POST',
  })
  if (!res.ok) {
    return parseError(res, 'Could not start chat')
  }
  const body = (await res.json()) as GoEnvelope<SupportSession>
  return asSession(unwrapData(body, 'No session data'))
}

export async function getThread(
  threadId: string,
  writeToken?: string | null,
): Promise<SupportSession> {
  const res = await supportRequest(
    API_ENDPOINTS.SUPPORT_THREAD_BY_ID(threadId),
    { method: 'GET', writeToken: writeToken ?? undefined },
  )
  if (!res.ok) {
    return parseError(res, 'Could not load chat')
  }
  const body = (await res.json()) as GoEnvelope<SupportSession>
  return asSession(unwrapData(body, 'No session data'))
}

export async function postChatMessage(
  threadId: string,
  text: string,
  writeToken?: string | null,
): Promise<SupportSession> {
  const res = await supportRequest(
    API_ENDPOINTS.SUPPORT_THREAD_CHAT(threadId),
    {
      method: 'POST',
      body: JSON.stringify({ text }),
      writeToken: writeToken ?? undefined,
    },
  )
  if (!res.ok) {
    return parseError(res, 'Could not send message')
  }
  const body = (await res.json()) as GoEnvelope<SupportSession>
  return asSession(unwrapData(body, 'No session data'))
}

export async function postSelectOption(
  threadId: string,
  optionId: string,
  writeToken?: string | null,
): Promise<SupportSession> {
  const res = await supportRequest(
    API_ENDPOINTS.SUPPORT_THREAD_SELECT(threadId),
    {
      method: 'POST',
      body: JSON.stringify({ optionId }),
      writeToken: writeToken ?? undefined,
    },
  )
  if (!res.ok) {
    return parseError(res, 'Action failed')
  }
  const body = (await res.json()) as GoEnvelope<SupportSession>
  return asSession(unwrapData(body, 'No session data'))
}

export async function postCloseThread(
  threadId: string,
  writeToken?: string | null,
): Promise<SupportSession> {
  const res = await supportRequest(API_ENDPOINTS.SUPPORT_THREAD_CLOSE(threadId), {
    method: 'POST',
    writeToken: writeToken ?? undefined,
  })
  if (!res.ok) {
    return parseError(res, 'Could not end chat')
  }
  const body = (await res.json()) as GoEnvelope<SupportSession>
  return asSession(unwrapData(body, 'No session data'))
}

export async function postRestart(
  threadId: string,
  writeToken?: string | null,
): Promise<SupportSession> {
  const res = await supportRequest(
    API_ENDPOINTS.SUPPORT_THREAD_RESTART(threadId),
    { method: 'POST', writeToken: writeToken ?? undefined },
  )
  if (!res.ok) {
    return parseError(res, 'Could not restart')
  }
  const body = (await res.json()) as GoEnvelope<SupportSession>
  return asSession(unwrapData(body, 'No session data'))
}

export async function getThreadListForUser(): Promise<SupportThreadListItem[]> {
  const res = await authenticatedFetch(API_BASE_URL + API_ENDPOINTS.SUPPORT_THREADS_BASE)
  if (!res.ok) {
    return parseError(res, 'Could not list chats')
  }
  const body = (await res.json()) as GoEnvelope<SupportThreadListItem[]>
  return (unwrapData(body, 'No data') as SupportThreadListItem[]) ?? []
}

export async function postClaimThread(
  threadId: string,
  writeToken: string,
): Promise<void> {
  const res = await authenticatedFetch(
    API_BASE_URL + API_ENDPOINTS.SUPPORT_THREAD_CLAIM(threadId),
    {
      method: 'POST',
      body: JSON.stringify({ writeToken }),
    },
  )
  if (!res.ok) {
    return parseError(res, 'Could not link chat')
  }
}

export async function postLookupThreads(
  entries: SupportLookupEntry[],
): Promise<SupportLookupResult[]> {
  const res = await supportRequest(
    API_ENDPOINTS.SUPPORT_THREADS_LOOKUP,
    {
      method: 'POST',
      body: JSON.stringify({ entries }),
    },
  )
  if (!res.ok) {
    return parseError(res, 'Could not look up chats')
  }
  const body = (await res.json()) as GoEnvelope<SupportLookupResult[]>
  return unwrapData(body, 'No lookup data') ?? []
}
